import { defineStore } from "pinia"
import {
  BenefitCodeEnum,
  CompanyBenefitDTO,
  CompanySubscription,
  CompanyYouthMembershipPlan,
  Entitlement,
  FeatureNameEnum,
  MedicalService
} from "@vitable/asclepius-api-client-ts"
import { useCompanyStore, type CompanyContextToken } from "@/store/company-store"
import { useGetActiveCompanySubscription } from "@/composables/subscription"
import { useGetCompanyBenefits, useGetUpcomingCompanyBenefits } from "@/modules/benefits/composables/benefit"
import { useGetCompanyEntitledMedicalServices, useGetCompanyEntitlements } from "@/composables/entitlements"
import { useGetActiveCompanyYouthMembershipPlan } from "@/composables/youth-membership"
import {
  ICHRA_BENEFIT_CODES,
  MEC_PLUS_BENEFIT_CODES,
  MVP_BENEFIT_CODES,
  VPC_ONLY_BENEFIT_CODES
} from "@common/utilities/benefit-utils"

export interface SubscriptionState {
  activeSubscription: Nullable<CompanySubscription>
  companyBenefits: Array<CompanyBenefitDTO>
  upcomingCompanyBenefits: Array<CompanyBenefitDTO>
  entitlements: Array<Entitlement>
  medicalServices: Array<MedicalService>
  activeCompanyYouthMembershipPlan: Nullable<CompanyYouthMembershipPlan>
}

export const useSubscriptionStore = defineStore("subscriptionStore", {
  persist: { storage: sessionStorage },
  state: (): SubscriptionState => {
    return {
      activeSubscription: null,
      companyBenefits: [],
      upcomingCompanyBenefits: [],
      entitlements: [],
      medicalServices: [],
      activeCompanyYouthMembershipPlan: null
    }
  },
  getters: {
    hasActiveSubscriptionIn(): boolean {
      return !!this.activeSubscription && !!this.activeSubscription.id
    },
    hasClinicPlanIn(): boolean {
      return this.entitlements.filter((e) => e.feature_name === FeatureNameEnum.OccupationalHealthClinics).length > 0
    },
    hasYouthClinicsIn(): boolean {
      return (
        this.entitlements.filter((e) => e.feature_name === FeatureNameEnum.OccupationalHealthYouthClinics).length > 0
      )
    },
    hasActiveCompanyYouthMembershipPlan(): boolean {
      return this.activeCompanyYouthMembershipPlan !== null
    },
    hasMedicationCoverageIn(): boolean {
      return this.entitlements.filter((e) => e.feature_name === FeatureNameEnum.FreeMedicationCoverage).length > 0
    },
    hasOccHealthPlanIn(): boolean {
      return this.entitlements.findIndex((e) => e.feature_name.toLowerCase().includes("occupational health")) >= 0
    },
    incrementalMembershipUpgradingAllowedIn(): boolean {
      return (
        this.entitlements.filter((e) => e.feature_name === FeatureNameEnum.IncrementalMembershipAllotmentUpgrading)
          .length > 0
      )
    },
    hasUnlimitedMemberships(): boolean {
      if (!this.hasActiveSubscriptionIn) return false

      return this.activeSubscription!.membership_allotment === null
    },
    hasMentalHealthCoverageIn(): boolean {
      if (!this.hasBenefitsIn) return false
      return (
        this.entitlements.filter(
          (e) => !!e.medical_service_name && e.medical_service_name.toLowerCase().includes("mental health")
        ).length > 0
      )
    },
    clinicAllotment(): number {
      const entitlement = this.entitlements.find((e) => e.feature_name === FeatureNameEnum.OccupationalHealthClinics)
      if (entitlement) return entitlement.quantity!
      return 0
    },

    occupationalHealthLabVoucherAllotment(): Optional<number | null> {
      return getEntitlementQuantity(FeatureNameEnum.OccupationalHealthLabCorpLabVouchers)
    },

    labCorpOccupationalHealthDrugAndAlcoholVoucherAllotment(): Optional<number | null> {
      return getEntitlementQuantity(FeatureNameEnum.OccupationalHealthLabCorpDrugAlcoholVouchers)
    },

    questOccupationalHealthLabVoucherAllotment(): Optional<number | null> {
      return getEntitlementQuantity(FeatureNameEnum.OccupationalHealthQuestLabVouchers)
    },

    urgentCareVoucherAllotment(): Optional<number | null> {
      return getEntitlementQuantity(FeatureNameEnum.OccupationalHealthUrgentCareVouchers)
    },

    hasLabOrderingFeature(): boolean {
      return (
        this.occupationalHealthLabVoucherAllotment != 0 ||
        this.questOccupationalHealthLabVoucherAllotment != 0 ||
        this.labCorpOccupationalHealthDrugAndAlcoholVoucherAllotment != 0
      )
    },

    hasServiceRequestingFeature(): boolean {
      return (
        this.occupationalHealthLabVoucherAllotment != 0 ||
        this.questOccupationalHealthLabVoucherAllotment != 0 ||
        this.labCorpOccupationalHealthDrugAndAlcoholVoucherAllotment != 0 ||
        this.urgentCareVoucherAllotment != 0
      )
    },

    hasBenefitsIn(): boolean {
      return this.companyBenefits.length > 0 || this.upcomingCompanyBenefits.length > 0
    },

    activeBenefitProductCodes(): Array<BenefitCodeEnum> {
      return this.companyBenefits.map((b) => b.type)
    },

    upcomingBenefitProductCodes(): Array<BenefitCodeEnum> {
      return this.upcomingCompanyBenefits.map((b) => b.type)
    },

    currentBenefitProductCodes(): Set<BenefitCodeEnum> {
      return new Set([...this.activeBenefitProductCodes, ...this.upcomingBenefitProductCodes])
    },

    hasMec(): boolean {
      return this.currentBenefitProductCodes.has(BenefitCodeEnum.Mec) || this.hasMecPlus || this.hasMvp
    },

    hasVpc(): boolean {
      return VPC_ONLY_BENEFIT_CODES.filter((c) => this.currentBenefitProductCodes.has(c)).length > 0
    },

    hasVpcEnhanced(): boolean {
      return this.currentBenefitProductCodes.has(BenefitCodeEnum.Vpc)
    },

    hasVpcCore(): boolean {
      return this.currentBenefitProductCodes.has(BenefitCodeEnum.VpcCore)
    },

    hasMecPlus(): boolean {
      return MEC_PLUS_BENEFIT_CODES.filter((c) => this.currentBenefitProductCodes.has(c)).length > 0
    },

    hasMvp(): boolean {
      return MVP_BENEFIT_CODES.filter((c) => this.currentBenefitProductCodes.has(c)).length > 0
    },

    hasIchra(): boolean {
      return ICHRA_BENEFIT_CODES.filter((c) => this.currentBenefitProductCodes.has(c)).length > 0
    },

    hasDental(): boolean {
      return this.currentBenefitProductCodes.has(BenefitCodeEnum.Vd)
    },

    hasVision(): boolean {
      return this.currentBenefitProductCodes.has(BenefitCodeEnum.Vv)
    },

    mecBenefit(): Optional<CompanyBenefitDTO> {
      return this.companyBenefits.find((b) => b.type === BenefitCodeEnum.Mec && !b.scheduled_for_cancellation_in)
    },
    upcomingMecBenefit(): Optional<CompanyBenefitDTO> {
      return this.upcomingCompanyBenefits.find(
        (b) => b.type === BenefitCodeEnum.Mec && !b.scheduled_for_cancellation_in
      )
    },
    mec2Benefit(): Optional<CompanyBenefitDTO> {
      return this.companyBenefits.find(
        (b) => MEC_PLUS_BENEFIT_CODES.filter((m) => b.type === m).length > 0 && !b.scheduled_for_cancellation_in
      )
    },
    upcomingMec2Benefit(): Optional<CompanyBenefitDTO> {
      return this.upcomingCompanyBenefits.find(
        (b) => MEC_PLUS_BENEFIT_CODES.filter((m) => b.type === m).length > 0 && !b.scheduled_for_cancellation_in
      )
    },

    mvpBenefit(): Optional<CompanyBenefitDTO> {
      return this.companyBenefits.find(
        (b) => MVP_BENEFIT_CODES.filter((m) => b.type === m).length > 0 && !b.scheduled_for_cancellation_in
      )
    },
    upcomingMvpBenefit(): Optional<CompanyBenefitDTO> {
      return this.upcomingCompanyBenefits.find(
        (b) => MVP_BENEFIT_CODES.filter((m) => b.type === m).length > 0 && !b.scheduled_for_cancellation_in
      )
    },

    ichraBenefit(): Optional<CompanyBenefitDTO> {
      return this.companyBenefits.find(
        (b) => ICHRA_BENEFIT_CODES.filter((m) => b.type === m).length > 0 && !b.scheduled_for_cancellation_in
      )
    },
    upcomingIchraBenefit(): Optional<CompanyBenefitDTO> {
      return this.upcomingCompanyBenefits.find(
        (b) => ICHRA_BENEFIT_CODES.filter((m) => b.type === m).length > 0 && !b.scheduled_for_cancellation_in
      )
    },

    // NOTE: Benefits of type BenefitCodeEnum.Mec have VPC Enhanced bundled in
    vpcEnhancedBenefit(): Optional<CompanyBenefitDTO> {
      return this.companyBenefits.find((b) => b.type === BenefitCodeEnum.Vpc)
    },
    upcomingVpcEnhancedBenefit(): Optional<CompanyBenefitDTO> {
      return this.upcomingCompanyBenefits.find((b) => b.type === BenefitCodeEnum.Vpc)
    },
    vpcCoreBenefit(): Optional<CompanyBenefitDTO> {
      return this.companyBenefits.find((b) => b.type === BenefitCodeEnum.VpcCore)
    },
    upcomingVpcCoreBenefit(): Optional<CompanyBenefitDTO> {
      return this.upcomingCompanyBenefits.find((b) => b.type === BenefitCodeEnum.VpcCore)
    },

    vpcBenefit(): Optional<CompanyBenefitDTO> {
      return this.companyBenefits.find((b) => b.type === BenefitCodeEnum.Vpc || b.type === BenefitCodeEnum.VpcCore)
    },

    hasActiveOrUpcomingAllowanceBasedBenefit(): boolean {
      const hasActiveAllowanceBased = this.companyBenefits.filter((b) => b.allowance_based_in).length > 0
      const hasUpcomingAllowanceBased = this.upcomingCompanyBenefits.filter((b) => b.allowance_based_in).length > 0
      return hasActiveAllowanceBased || hasUpcomingAllowanceBased
    },

    closestUpcomingOpenEnrollmentBenefit(): Nullable<CompanyBenefitDTO> {
      const cbs = this.upcomingCompanyBenefits.sort((a, b) => {
        const startDateA = new Date(a.open_enrollment_start).getTime()
        const startDateB = new Date(b.open_enrollment_start).getTime()

        if (startDateA === startDateB) {
          // Priorty sort the benefits with an open enrollment end date
          return b.open_enrollment_end ? 1 : -1
        } else {
          return startDateA - startDateB
        }
      })

      if (!cbs.length) return null
      return cbs[0]
    },

    mostRecentOpenEnrollmentStartedBenefit(): Nullable<CompanyBenefitDTO> {
      const cbs = this.companyBenefits.sort(
        (a, b) => new Date(b.open_enrollment_start).getDate() - new Date(a.open_enrollment_start).getDate()
      )

      if (!cbs.length) return null

      return cbs[0]
    },

    primaryActiveBenefit(): Nullable<CompanyBenefitDTO> {
      if (this.ichraBenefit) return this.ichraBenefit
      if (this.mvpBenefit) return this.mvpBenefit
      if (this.mec2Benefit) return this.mec2Benefit
      if (this.mecBenefit) return this.mecBenefit
      if (this.vpcBenefit) return this.vpcBenefit

      return null
    },

    relevantPlanYears(): Array<CompanyBenefitDTO> {
      const planYearsById = new Map<string, CompanyBenefitDTO>()
      for (const benefit of [...this.companyBenefits, ...this.upcomingCompanyBenefits]) {
        planYearsById.set(benefit.id, benefit)
      }
      return [...planYearsById.values()]
    },

    planYearsMissingEligibilityPolicy(): Array<CompanyBenefitDTO> {
      return this.relevantPlanYears.filter((benefit) => !benefit.eligibility_policy && !benefit.configured_in)
    },

    hasMissingPlanYearEligibilityPolicies(): boolean {
      return this.planYearsMissingEligibilityPolicy.length > 0
    },

    hasAnyPlanYearEligibilityPolicy(): boolean {
      return this.relevantPlanYears.some((benefit) => !!benefit.eligibility_policy)
    },

    hasCompletePlanYearEligibilityPolicyConfiguration(): boolean {
      return this.hasAnyPlanYearEligibilityPolicy && !this.hasMissingPlanYearEligibilityPolicies
    },

    hasHrPlatformIntegrationPermission(): boolean {
      return this.hasBenefitsIn && this.hasUnlimitedMemberships
    },

    hasAcaAutopilotIn(): boolean {
      return this.entitlements.some((entitlement) => entitlement.feature_name === FeatureNameEnum.AcaAutopilot)
    }
  },

  actions: {
    async loadActiveCompanySubscription(contextToken?: CompanyContextToken) {
      const companyStore = useCompanyStore()
      const token = contextToken ?? companyStore.getCompanyContextToken()
      if (!token || !companyStore.isCompanyContextCurrent(token)) return

      const { getActiveCompanySubscription, companySubscription } = useGetActiveCompanySubscription()

      await getActiveCompanySubscription({
        companyId: token.companyId
      })

      if (!companyStore.isCompanyContextCurrent(token)) return
      this.activeSubscription = companySubscription.value
    },

    async loadEntitlements(contextToken?: CompanyContextToken) {
      const companyStore = useCompanyStore()
      const token = contextToken ?? companyStore.getCompanyContextToken()
      if (!token || !companyStore.isCompanyContextCurrent(token)) return

      const { entitlements, getCompanyEntitlements } = useGetCompanyEntitlements()
      await getCompanyEntitlements(token.companyId)
      if (!companyStore.isCompanyContextCurrent(token)) return
      this.entitlements = entitlements.value
    },

    async loadBenefits(contextToken?: CompanyContextToken) {
      const companyStore = useCompanyStore()
      const token = contextToken ?? companyStore.getCompanyContextToken()
      if (!token || !companyStore.isCompanyContextCurrent(token)) return

      const { benefits, getCompanyBenefits } = useGetCompanyBenefits()
      const { benefits: upcomingBenefits, getUpcomingCompanyBenefits } = useGetUpcomingCompanyBenefits()
      await Promise.all([getCompanyBenefits(token.companyId), getUpcomingCompanyBenefits(token.companyId)])
      if (!companyStore.isCompanyContextCurrent(token)) return
      this.companyBenefits = benefits.value
      this.upcomingCompanyBenefits = upcomingBenefits.value
    },

    async loadMedicalServices(contextToken?: CompanyContextToken) {
      const companyStore = useCompanyStore()
      const token = contextToken ?? companyStore.getCompanyContextToken()
      if (!token || !companyStore.isCompanyContextCurrent(token)) return

      const { medicalServices, getCompanyEntitledMedicalServices } = useGetCompanyEntitledMedicalServices()
      await getCompanyEntitledMedicalServices(token.companyId)
      if (!companyStore.isCompanyContextCurrent(token)) return
      this.medicalServices = medicalServices.value
    },

    async loadActiveCompanyYouthMembershipPlan(contextToken?: CompanyContextToken) {
      const companyStore = useCompanyStore()
      const token = contextToken ?? companyStore.getCompanyContextToken()
      if (!token || !companyStore.isCompanyContextCurrent(token)) return

      const { getActiveCompanyYouthMembershipPlan, activeCompanyYouthMembershipPlan } =
        useGetActiveCompanyYouthMembershipPlan()
      await getActiveCompanyYouthMembershipPlan(token.companyId)
      if (!companyStore.isCompanyContextCurrent(token)) return
      this.activeCompanyYouthMembershipPlan = activeCompanyYouthMembershipPlan.value ?? null
    }
  }
})

function getEntitlementQuantity(featureName: FeatureNameEnum): Optional<number | null> {
  const store = useSubscriptionStore()
  const entitlement = store.entitlements.find((e) => e.feature_name === featureName)
  if (entitlement) return entitlement.quantity!
  return 0
}
